import api from './axios';

interface RegisterData {
  email: string;
  password: string;
  full_name: string;
  company?: string | null;
}

interface LoginData {
  email: string;
  password: string;
}

interface AuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface ResetPasswordData {
  token: string;
  new_password: string;
}

function formatAuthError(error: any): string {
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (detail) return detail;
  if (status === 401) return 'Incorrect email or password';
  if (status === 403) return 'Account is not verified or inactive';
  return error?.message || 'Authentication request failed';
}

export const register = async (data: RegisterData) => {
  try {
    const response = await api.post('/api/auth/register', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const login = async (data: LoginData): Promise<AuthResponse> => {
  try {
    const response = await api.post<AuthResponse>('/api/auth/token', {
      username: data.email,
      password: data.password,
      grant_type: 'password'
    }, {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    if (response.data.access_token) {
      // Store tokens
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      return response.data;
    }

    throw new Error('Login failed: missing access token');
  } catch (error) {
    const err = error as any;
    console.warn('[AuthService] Login error:', {
      message: formatAuthError(error),
      status: err?.response?.status,
      path: err?.config?.url,
    });
    throw error;
  }
};

export const logout = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
  // The session cookie will be cleared by the browser when we navigate away
  window.location.href = '/login';
};

export const refreshToken = async (): Promise<string> => {
  try {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      throw new Error('No refresh token available');
    }

    const response = await api.post<AuthResponse>('/api/auth/refresh', {
      token: refresh_token
    }, {
      withCredentials: true
    });

    if (response.data.access_token) {
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      return response.data.access_token;
    }

    throw new Error('Failed to refresh token');
  } catch (error) {
    console.error('[AuthService] Token refresh error:', error);
    logout(); // Force logout on refresh failure
    throw error;
  }
};

export const resetPassword = async (data: ResetPasswordData) => {
  try {
    const response = await api.post('/api/auth/reset-password', data);
    return response.data;
  } catch (error) {
    throw error;
  }
};
